import { Permission } from '@today/types'
import {
	AppHeader,
	AppSidebar,
	type AppSidebarItem,
	type SidebarLinkProps,
	UserMenu,
	type UserMenuEntry,
} from '@today/ui-components'
import { PlaneShellFrame } from '@today/ui-domain'
import type { ReactElement } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useViewerRole } from '@/auth/useViewerRole'
import { BuildInfoBar } from '@/components/Layout/BuildInfoBar'
import { useAuth } from '@/hooks/useAuth'
import { useLanguagePreference } from '@/hooks/useLanguagePreference'
import { activeNavTarget, WORKSPACE_NAV_GROUPS, workspaceNavPredicates } from '@/navigation/workspace'

enum AccountAction {
	SETTINGS = 'settings',
	SIGN_OUT = 'signout',
}

const ACCOUNT_ENTRIES: UserMenuEntry[] = [
	{ id: AccountAction.SETTINGS, label: 'Settings' },
	{ id: AccountAction.SIGN_OUT, label: 'Sign out' },
]

const NEW_OFFER_CLASSES = 'rounded-ds-md bg-ds-primary px-3 py-1.5 text-ds-sm font-medium text-ds-on-primary'

function renderRouterLink(_item: AppSidebarItem, props: SidebarLinkProps): ReactElement {
	return (
		<Link to={props.target} aria-current={props['aria-current']} className={props.className}>
			{props.children}
		</Link>
	)
}

/** The workspace sidebar: the plane's nav, filtered by the viewer's atoms, plus the build-info bar. */
function WorkspaceSidebar(): ReactElement {
	const { role } = useViewerRole()
	const { permissions } = useViewerPermissions()
	const { pathname } = useLocation()
	// AppSidebar marks exact matches only; resolving the section here keeps it
	// highlighted on nested pages (`/customers/42/edit` → Customers).
	const currentPath = activeNavTarget(pathname, WORKSPACE_NAV_GROUPS) ?? pathname

	return (
		<div className="flex h-full flex-col gap-ds-4">
			<AppSidebar
				groups={WORKSPACE_NAV_GROUPS}
				permissions={permissions}
				predicates={workspaceNavPredicates(role)}
				currentPath={currentPath}
				label="Workspace navigation"
				renderLink={renderRouterLink}
			/>
			<BuildInfoBar />
		</div>
	)
}

/** The account menu, or nothing while there is no signed-in user object. */
function WorkspaceAccountMenu(): ReactElement | null {
	const { user, logout } = useAuth()
	const navigate = useNavigate()
	if (!user) return null

	// Profile claims can be absent (a federated IdP token may omit name/email).
	const label = user.displayName || user.email || 'Account'
	function handleAction(id: string): void {
		if (id === AccountAction.SETTINGS) void navigate('/settings')
		if (id === AccountAction.SIGN_OUT) logout()
	}
	return <UserMenu label={label} entries={ACCOUNT_ENTRIES} onAction={handleAction} />
}

/** The workspace header: the plane title, the offer.write-gated "New offer" action and the account menu. */
function WorkspaceHeader(): ReactElement {
	const { permissions } = useViewerPermissions()
	const mayWriteOffers = permissions.includes(Permission.OFFER_WRITE)
	const newOffer = mayWriteOffers ? (
		<Link to="/offers/new" className={NEW_OFFER_CLASSES}>
			New offer
		</Link>
	) : undefined

	return <AppHeader title="Workspace" actions={newOffer} userMenu={<WorkspaceAccountMenu />} />
}

/**
 * The workspace (sell-flow) plane shell (TO-636, AD-4): `PlaneShellFrame` bound to
 * the workspace nav, header and account menu, wrapping the routed page.
 *
 * @remarks
 * A thin data+policy binder — every region is the shared TO-633 substrate. It
 * also carries over what the shared `Layout` did for every authenticated page:
 * the signed-in user's saved UI language (TO-197) and the build-info bar.
 * Authorization stays with the route guards; the nav only omits what the
 * viewer's atoms do not permit. The realtime notification region is NOT mounted
 * here: the root `ToastProvider` renders the single app-wide
 * `NotificationStatusBarContainer` for every route (TO-644), so passing the
 * frame's `toasts` slot would duplicate it — like `PlatformShell`, the shell
 * leaves that slot empty.
 */
export function WorkspaceShell(): ReactElement {
	useLanguagePreference()

	return (
		<PlaneShellFrame sidebar={<WorkspaceSidebar />} header={<WorkspaceHeader />}>
			<Outlet />
		</PlaneShellFrame>
	)
}
